/**
 * The college chip — the NFL club chip's twin, for the farm system.
 *
 * `NflChip.tsx` is the pattern this file mirrors, deliberately and line for line: white ink, the
 * school's primary at full strength as the fill, the secondary as a 2px RIGHT border, radius 4.
 * A college chip reads as a club chip because it answers the same question — which team — and the
 * app has exactly one grammar for that. The contrast check reuses `contrastRatio` from that file
 * rather than copying it, so there is one derivation of the number both tables are judged by.
 *
 * THREE RULES THIS FILE EXISTS TO ENFORCE, each from a documented past failure:
 *
 *   NO FALLBACK CHIP. An unmapped school throws in development. The bug this prevents is a
 *   missing lookup rendering as a plausible slate-grey chip that nobody notices for a season.
 *   `tests/collegeChips.test.ts` walks every school in the farm log and fails on a gap.
 *
 *   SCHOOLS ARE SPELLED OUT, at every breakpoint, phone included. The only letter-forms permitted
 *   are the fourteen whose letters ARE the name (`LETTER_FORMS` below). When a row is tight the
 *   PLAYER NAME truncates — never the school. Hence `flex-shrink:0` on the chip.
 *
 *   THE FOUR POSITION HUES ARE NOT OURS. 210/150/30/265 belong to the position chip and 191-teal
 *   belongs to the outcome chip. Nothing here may use them.
 *
 * Colours come from `handoff/chip-system.md`, which is normative: the 77 rows in its table are
 * reproduced below character for character, in its order, and `tests/collegeChips.test.ts` diffs
 * the two. Four rows the table does not carry are added under ADDITIONS, each with its source.
 */

/** fill = school primary, edge = secondary (the right border). Ink is white on all of them. */
export const COLLEGE_CHIPS: Readonly<Record<string, { fill: string; edge: string }>> = {
  /* -- the 77 rows of chip-system.md, verbatim and in its order ---------------------------- */
  'Air Force':         { fill: '#003087', edge: '#B1B3B3' },
  'Alabama':           { fill: '#9E1B32', edge: '#828A8F' },
  'Arizona':           { fill: '#003366', edge: '#CC0033' },
  'Arizona State':     { fill: '#8C1D40', edge: '#FFC627' },
  'Arkansas':          { fill: '#9D2235', edge: '#000000' },
  'Army':              { fill: '#000000', edge: '#D4BF91' },
  'Auburn':            { fill: '#0C2340', edge: '#E87722' },
  'Baylor':            { fill: '#154734', edge: '#FFB81C' },
  'Boise State':       { fill: '#0033A0', edge: '#D64309' },
  'Boston College':    { fill: '#98002E', edge: '#BC9B6A' },
  'BYU':               { fill: '#002E5D', edge: '#FFFFFF' },
  'Cal':               { fill: '#003262', edge: '#FDB515' },
  'Cincinnati':        { fill: '#E00122', edge: '#000000' },
  'Clemson':           { fill: '#F66733', edge: '#522D80' },
  'Colorado':          { fill: '#CFB87C', edge: '#000000' },
  'Duke':              { fill: '#003087', edge: '#FFFFFF' },
  'Florida':           { fill: '#0021A5', edge: '#FA4616' },
  'Florida State':     { fill: '#782F40', edge: '#CEB888' },
  'Georgia':           { fill: '#BA0C2F', edge: '#000000' },
  'Georgia Tech':      { fill: '#B3A369', edge: '#003057' },
  'Illinois':          { fill: '#13294B', edge: '#FF5F05' },
  'Indiana':           { fill: '#990000', edge: '#EEEDEB' },
  'Iowa':              { fill: '#FFCD00', edge: '#000000' },
  'Iowa State':        { fill: '#C8102E', edge: '#F1BE48' },
  'Kansas':            { fill: '#0051BA', edge: '#E8000D' },
  'Kansas State':      { fill: '#512888', edge: '#A7A7A7' },
  'Kentucky':          { fill: '#0033A0', edge: '#FFFFFF' },
  'Louisville':        { fill: '#AD0000', edge: '#000000' },
  'LSU':               { fill: '#461D7C', edge: '#FDD023' },
  'Maryland':          { fill: '#E03A3E', edge: '#FFD520' },
  'Memphis':           { fill: '#003087', edge: '#898D8D' },
  'Miami':             { fill: '#F47321', edge: '#005030' },
  'Michigan':          { fill: '#00274C', edge: '#FFCB05' },
  'Michigan State':    { fill: '#18453B', edge: '#FFFFFF' },
  'Minnesota':         { fill: '#7A0019', edge: '#FFCC33' },
  'Mississippi State': { fill: '#660000', edge: '#FFFFFF' },
  'Missouri':          { fill: '#000000', edge: '#F1B82D' },
  'Navy':              { fill: '#00205B', edge: '#C5B783' },
  'Nebraska':          { fill: '#E41C38', edge: '#FDF6F5' },
  'North Carolina':    { fill: '#4B9CD3', edge: '#13294B' },
  'NC State':          { fill: '#CC0000', edge: '#000000' },
  'Northwestern':      { fill: '#4E2A84', edge: '#FFFFFF' },
  'Notre Dame':        { fill: '#0C2340', edge: '#C99700' },
  'Ohio State':        { fill: '#BB0000', edge: '#A7B1B7' },
  'Oklahoma':          { fill: '#841617', edge: '#FDF9D8' },
  'Oklahoma State':    { fill: '#FF7300', edge: '#000000' },
  'Ole Miss':          { fill: '#14213D', edge: '#CE1126' },
  'Oregon':            { fill: '#154733', edge: '#FEE123' },
  'Oregon State':      { fill: '#DC4405', edge: '#000000' },
  'Penn State':        { fill: '#041E42', edge: '#FFFFFF' },
  'Pittsburgh':        { fill: '#003594', edge: '#FFB81C' },
  'Purdue':            { fill: '#CEB888', edge: '#000000' },
  'Rutgers':           { fill: '#CC0033', edge: '#5F6A72' },
  'SMU':               { fill: '#354CA1', edge: '#C8102E' },
  'South Carolina':    { fill: '#73000A', edge: '#000000' },
  'Stanford':          { fill: '#8C1515', edge: '#4D4F53' },
  'Syracuse':          { fill: '#F76900', edge: '#000E54' },
  'TCU':               { fill: '#4D1979', edge: '#A3A9AC' },
  'Tennessee':         { fill: '#FF8200', edge: '#58595B' },
  'Texas':             { fill: '#BF5700', edge: '#FFFFFF' },
  'Texas A&M':         { fill: '#500000', edge: '#FFFFFF' },
  'Texas Tech':        { fill: '#CC0000', edge: '#000000' },
  'UCF':               { fill: '#000000', edge: '#BA9B37' },
  'UCLA':              { fill: '#2D68C4', edge: '#F2A900' },
  'UNLV':              { fill: '#CF0A2C', edge: '#666666' },
  'USC':               { fill: '#990000', edge: '#FFC72C' },
  'Utah':              { fill: '#CC0000', edge: '#808080' },
  'UTEP':              { fill: '#041E42', edge: '#FF8200' },
  'Vanderbilt':        { fill: '#866D4B', edge: '#000000' },
  'Villanova':         { fill: '#002664', edge: '#13B5EA' },
  'Virginia':          { fill: '#232D4B', edge: '#F84C1E' },
  'Virginia Tech':     { fill: '#630031', edge: '#CF4420' },
  'Wake Forest':       { fill: '#9E7E38', edge: '#000000' },
  'Washington':        { fill: '#4B2E83', edge: '#B7A57A' },
  'Washington State':  { fill: '#981E32', edge: '#5E6A71' },
  'West Virginia':     { fill: '#002855', edge: '#EAAA00' },
  'Wisconsin':         { fill: '#C5050C', edge: '#FFFFFF' },

  /*
   * -- ADDITIONS -------------------------------------------------------------------------------
   *
   * Four schools reachable from the farm log that `chip-system.md` does not carry. Signed off by
   * Tim on 21 Aug 2026 ("do some placeholder color options if needed for teams without a color
   * scheme and the high school / if there's a missing school, do research to figure out what
   * school it should be"). Each carries the source it was taken from, because the standing rule
   * is that a colour nobody can trace is a colour somebody invented.
   */

  // Jaret Patterson, Vishal's 2020 pick. UB Blue is the primary on the university's own athletics
  // brand page; Bulls Black is the first of its three secondaries.
  // https://www.buffalo.edu/brand/identity/Athletics/ColorsType.html
  'Buffalo': { fill: '#005BBB', edge: '#000000' },

  // Corey Davis, John's 2016 pick. WMU publishes gold as the dominant colour, but white ink on
  // #F1C500 is unreadable at chip size (1.6:1), so the pair is inverted the way every other
  // light-primary school in the table above is. Brown fill, gold edge.
  // https://wmich.edu/brand/visualidentity/color
  'Western Michigan': { fill: '#532E1F', edge: '#F1C500' },

  // MIT is named in the permitted letter-forms in both handoff documents but has no colour row,
  // so it could be drawn and not built. These are MIT's current published core colours — note
  // that the widely-remembered #A31F34 is the LEGACY red; brand.mit.edu now publishes #750014.
  // https://brand.mit.edu/color
  'MIT': { fill: '#750014', edge: '#8B959E' },

  /*
   * Trey Grant, Samant's 2023 pick — a Virginia HIGH SCHOOL, not a college. He never turned up on
   * a college roster and was written off by commissioner ruling on 27 Aug 2025.
   *
   * This is the one row with no researched source, and that is the point: dressing a high school
   * in invented school colours would make the chip claim an authority it does not have. These two
   * are app inks (`--ink-3` and `--ink-4`), so the chip reads as deliberately neutral — a real
   * mapped row, visibly not a college. It is NOT a fallback: nothing else resolves here, and an
   * unmapped school still throws.
   */
  'Trinity Episcopal (VA)': { fill: '#55607a', edge: '#8a93a6' },
};

/**
 * The only schools that appear as letters, because the letters ARE the name. Anything not in this
 * set is written out in full at every breakpoint — "South Carolina", not "S. Carolina", not "SC",
 * not even when the row is tight. `tests/collegeChips.test.ts` asserts this on RENDERED text.
 */
export const LETTER_FORMS: ReadonlySet<string> = new Set([
  'USC', 'UCLA', 'LSU', 'MIT', 'TCU', 'BYU', 'SMU', 'Cal', 'UCF', 'UNLV',
  'Army', 'Navy', 'Air Force', 'UTEP',
]);

/**
 * Fills that white ink does not clear 4.5:1 on, accepted deliberately.
 *
 * Same pin `nflChips.test.ts` puts on CIN/LAR/NO and for the same reason: dimming Iowa's gold or
 * Tennessee orange far enough to pass stops the chip reading as that school, and the colours come
 * from `chip-system.md`, which is normative and says so. Thirteen of them, all in the documented
 * table — none of the four ADDITIONS is low. Pinned in both directions and DERIVED, not typed:
 * the test recomputes every ratio with `contrastRatio` from `NflChip.tsx`, so darkening one of
 * these to "fix" it fails just as surely as adding a new low chip does.
 */
export const ACCEPTED_LOW_CONTRAST: readonly string[] = [
  'Clemson',        // 3.04 — Clemson orange
  'Colorado',       // 1.94 — Colorado gold
  'Georgia Tech',   // 2.51 — old gold
  'Iowa',           // 1.50 — Iowa gold, the lowest on the board
  'Maryland',       // 4.34
  'Miami',          // 2.87 — Miami orange
  'North Carolina', // 3.00 — Carolina blue
  'Oklahoma State', // 2.73 — Cowboy orange
  'Oregon State',   // 4.31
  'Purdue',         // 1.94 — old gold
  'Syracuse',       // 3.01 — Syracuse orange
  'Tennessee',      // 2.49 — Tennessee orange
  'Wake Forest',    // 3.82 — old gold
];

/* -- the chip ------------------------------------------------------------------------------- */

/**
 * Throws in development, renders nothing in production.
 *
 * The alternative — a grey chip — is the exact bug §0.4 of the farm brief was written about: a
 * missing lookup that looks like a design choice. In production a farm row with no chip is still
 * wrong, but it is visibly missing rather than quietly plausible, and the test below catches it
 * long before production does.
 */
function unknownSchool(school: string): null {
  const message = `No chip for school "${school}" — add a row to COLLEGE_CHIPS (see chip-system.md).`;
  if (import.meta.env?.DEV) throw new Error(message);
  console.error(message);
  return null;
}

/**
 * `.kchip club` is not a typo and not a shortcut: it is the SAME class the NFL club chip on a
 * Declare row renders. One declaration block, one set of numbers, so "a college chip reads as a
 * club chip" is enforced by there being nothing to drift — restyling the club chip restyles this
 * one, in the same commit, on both screens. That is what §0.1 asks for.
 */
export function CollegeChip({ school, title }: { school: string; title?: string }) {
  const c = COLLEGE_CHIPS[school];
  if (!c) return unknownSchool(school);
  return (
    <span
      className="kchip club"
      title={title ?? school}
      style={{ background: c.fill, borderRightColor: c.edge }}
    >
      {school}
    </span>
  );
}
